import { createContext, useContext, useEffect, useState, useMemo } from 'react'
import { useTranslate } from 'react-admin'
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import PropTypes from 'prop-types'
import decodeJwt from 'jwt-decode'
import { API_ORIGIN } from '../utils/constants/url'
import localStorageIds from '../utils/constants/localStorageIds'

const CourtStatusHubContext = createContext(null)

// Heartbeats arrive every ~20 s and nothing is sent for a court with no recent data, so a status
// not refreshed for 3 beats is dropped and the court shows as unknown.
export const STALE_AFTER_MS = 60_000
const STALE_SWEEP_MS = 10_000

export const useCourtStatusHubContext = () => useContext(CourtStatusHubContext)

// Follows the org's courts: orgId must be the org they were listed for, as the hub refuses another org's.
export const CourtStatusHubProvider = ({ children, orgId, courtIds = [] }) => {
  const [connection, setConnection] = useState(null)
  const [connectionError, setConnectionError] = useState(null)
  const [courtStatuses, setCourtStatuses] = useState({})
  const translate = useTranslate()

  useEffect(() => {
    // Only create connection if we have court IDs
    if (!orgId || courtIds.length === 0) {
      return undefined
    }

    const storedToken = localStorage.getItem(localStorageIds.TOKEN)
    const token = storedToken ? decodeJwt(storedToken) : null
    const userId = token && token.sub ? token.sub : null
    let conn = null

    const initializeCourtStatusHub = async () => {
      try {
        if (token && userId) {
          const courtIdsParam = courtIds.join(',')
          const connectionUrl = `${API_ORIGIN}/v1/orgs/${orgId}/courts/status/live?courtIds=${courtIdsParam}`

          conn = new HubConnectionBuilder()
            .withUrl(connectionUrl, {
              accessTokenFactory: () => {
                return localStorage.getItem(localStorageIds.TOKEN)
              },
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.None)
            .build()

          conn.onclose((error) => {
            if (error) {
              setConnectionError(translate('ra.notification.serverError'))
            } else {
              setConnectionError(null)
            }
          })

          // The courts followed, sent on joining; each one's status arrives on joining and as it is checked.
          conn.on('Following', () => {})

          conn.on('StatusUpdate', (statusUpdate) => {
            const courtId = statusUpdate.courtId

            if (!courtId) {
              return
            }

            setCourtStatuses((prev) => ({
              ...prev,
              [courtId]: {
                isUp: statusUpdate.success,
                checkResults: statusUpdate.checkResults,
                receivedAt: Date.now(),
              },
            }))
          })

          await conn.start()
          setConnection(conn)
        }
      } catch (error) {
        setConnectionError(translate('ra.notification.serverError'))
      }
    }

    initializeCourtStatusHub()

    const sweep = setInterval(() => {
      setCourtStatuses((prev) => {
        const now = Date.now()
        const fresh = Object.fromEntries(Object.entries(prev).filter(([, status]) => now - status.receivedAt < STALE_AFTER_MS))
        return Object.keys(fresh).length === Object.keys(prev).length ? prev : fresh
      })
    }, STALE_SWEEP_MS)

    return () => {
      clearInterval(sweep)
      if (conn) {
        conn.stop()
      }
    }
  }, [orgId, courtIds, translate])

  const contextValue = useMemo(
    () => ({
      connection,
      connectionError,
      courtStatuses,
      isConnected: connection?.state === 'Connected',
    }),
    [connection, connectionError, courtStatuses],
  )

  return (
    <CourtStatusHubContext.Provider value={contextValue}>{children}</CourtStatusHubContext.Provider>
  )
}

CourtStatusHubProvider.propTypes = {
  children: PropTypes.node.isRequired,
  orgId: PropTypes.string,
  courtIds: PropTypes.arrayOf(PropTypes.string),
}
